import { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { TClass } from "../../../apis/academic/academic.interfaces";
import {
  useCreateClassHook,
  useUpdateClassHook,
  useCourseHook,
  useClassHook,
} from "../../../hooks/useAcademicHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { hasCoursePrerequisite } from "./academic.helpers";

const classSchema = z.object({
  courseId: z.string().min(1, "Course is required"),
  name: z
    .string()
    .min(1, "Class name is required")
    .min(2, "Class name must be at least 2 characters")
    .max(200, "Class name must not exceed 200 characters"),
  grade: z
    .string()
    .min(1, "Grade label is required")
    .max(120, "Grade label must not exceed 120 characters"),
  academicYear: z
    .string()
    .min(2, "Academic year is required")
    .max(32, "Academic year must not exceed 32 characters"),
  strength: z.coerce
    .number({ invalid_type_error: "Strength must be a number" })
    .int("Strength must be a whole number")
    .min(0, "Strength cannot be negative")
    .optional(),
});

type ClassFormData = z.infer<typeof classSchema>;

interface ManageClassDialogProps {
  open: boolean;
  onClose: () => void;
  classItem?: TClass | null;
}

const ManageClassDialog: FC<ManageClassDialogProps> = ({
  open,
  onClose,
  classItem,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createClassMutation = useCreateClassHook();
  const updateClassMutation = useUpdateClassHook();
  const { data: courses } = useCourseHook();
  const { data: classes } = useClassHook();
  const isEditMode = !!classItem;
  const hasCourses = hasCoursePrerequisite(courses);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      courseId: "",
      name: "",
      grade: "",
      academicYear: "",
      strength: undefined,
    },
  });

  const watchedCourseId = watch("courseId");
  const watchedGrade = watch("grade");
  const watchedYear = watch("academicYear");

  useEffect(() => {
    if (classItem) {
      reset({
        courseId: classItem.courseId,
        name: classItem.name,
        grade: classItem.grade,
        academicYear: classItem.academicYear,
        strength: classItem.strength,
      });
    } else {
      reset({
        courseId: courses?.[0]?.id ?? "",
        name: "",
        grade: "",
        academicYear: "",
        strength: undefined,
      });
    }
  }, [classItem, reset, open, courses]);

  const courseOptions =
    courses?.map((c) => ({
      id: c.id,
      label: c.name,
    })) || [];

  const validateUniqueCohort = (
    courseId: string,
    grade: string,
    academicYear: string,
  ): boolean => {
    if (!classes) return true;
    if (
      isEditMode &&
      classItem &&
      classItem.courseId === courseId &&
      classItem.grade === grade &&
      classItem.academicYear === academicYear
    ) {
      return true;
    }
    return !classes.some(
      (c) =>
        c.courseId === courseId &&
        c.grade === grade &&
        c.academicYear === academicYear,
    );
  };

  const cohortError =
    watchedCourseId &&
    watchedGrade &&
    watchedYear &&
    !validateUniqueCohort(watchedCourseId, watchedGrade, watchedYear)
      ? "A class with this course, grade label, and academic year already exists."
      : undefined;

  const onInvalid = (validationErrors: typeof errors) => {
    const firstMessage = Object.values(validationErrors)
      .map((e) => (e as { message?: string } | undefined)?.message)
      .find((m): m is string => !!m);
    logger.show_error(
      firstMessage ?? "Please fix the highlighted fields before saving.",
    );
  };

  const onSubmit = async (data: ClassFormData) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before creating or editing a class.",
      );
      return;
    }

    if (!validateUniqueCohort(data.courseId, data.grade, data.academicYear)) {
      logger.show_error(
        "Another class already uses this course with the same grade label and academic year.",
      );
      return;
    }

    try {
      if (isEditMode && classItem) {
        await updateClassMutation.mutateAsync({
          id: classItem.id,
          request: {
            name: data.name,
            grade: data.grade,
            academicYear: data.academicYear,
            strength: data.strength,
          },
        });
        logger.show_success("Class updated successfully!");
      } else {
        await createClassMutation.mutateAsync({
          courseId: data.courseId,
          request: {
            name: data.name,
            grade: data.grade,
            academicYear: data.academicYear,
            strength: data.strength,
          },
        });
        logger.show_success("Class created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} class. Please try again.`,
      );
    }
  };

  const isLoading =
    isSubmitting ||
    createClassMutation.isPending ||
    updateClassMutation.isPending;
  const isError = createClassMutation.isError || updateClassMutation.isError;
  const errorMessage =
    createClassMutation.error?.message || updateClassMutation.error?.message;

  if (!selectedInstitute) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-class-dialog"
    >
      <DialogTitle data-testid="manage-class-dialog-title">
        {isEditMode ? "Edit Class/Standard" : "Add New Class/Standard"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        data-testid="class-form"
      >
        <DialogContent data-testid="class-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="class-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} class. Please try again.`}
            </Alert>
          )}

          {cohortError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {cohortError}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="class-form-container"
          >
            {!isEditMode && (
              <Grid item xs={12}>
                {!hasCourses ? (
                  <Alert severity="info" data-testid="no-courses-message">
                    Add at least one course first — classes are created inside a
                    course stream.
                  </Alert>
                ) : (
                  <Controller
                    name="courseId"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        disablePortal
                        options={courseOptions}
                        value={
                          courseOptions.find((o) => o.id === field.value) ??
                          null
                        }
                        onChange={(_, v) => field.onChange(v?.id ?? "")}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Course"
                            required
                            error={!!errors.courseId}
                            helperText={errors.courseId?.message}
                            size="small"
                          />
                        )}
                        data-testid="class-course-select"
                      />
                    )}
                  />
                )}
              </Grid>
            )}

            {isEditMode && classItem && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Course (fixed):{" "}
                  {courses?.find((c) => c.id === classItem.courseId)?.name ??
                    classItem.courseId}
                </Typography>
              </Grid>
            )}

            <ControlledTextField
              control={control as any}
              name="name"
              id="class-name"
              label="Class display name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "class-name-input" }}
            />

            <ControlledTextField
              control={control as any}
              name="grade"
              id="class-grade"
              label="Grade label"
              required
              helperText='Free-form label for this cohort (e.g. "6", "Senior-1").'
              errorMessage={errors.grade?.message}
              inputProps={{ "data-testid": "class-grade-input" }}
            />

            <ControlledTextField
              control={control as any}
              name="academicYear"
              id="class-academic-year"
              label="Academic year"
              required
              errorMessage={errors.academicYear?.message}
              inputProps={{ "data-testid": "class-academic-year-input" }}
            />

            <ControlledTextField
              control={control as any}
              name="strength"
              id="class-strength"
              label="Expected strength (optional)"
              errorMessage={errors.strength?.message}
              inputProps={{ "data-testid": "class-strength-input" }}
            />
          </Grid>
        </DialogContent>
        <DialogActions data-testid="class-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-class-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isLoading ||
              !!cohortError ||
              (!isEditMode && !hasCourses)
            }
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-class-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Class"
                : "Create Class"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageClassDialog;
